/**
 * Certificate PDF helpers — inline images as data URLs, then capture full-bleed A4.
 */

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { CERT_A4_HEIGHT_PX, CERT_A4_WIDTH_PX } from "./certificateTemplate";

const PLACEHOLDER_GIF =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

/**
 * Same-origin public base for packaged certificate assets.
 * @returns {string}
 */
const publicCertBase = () => {
  const home = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${home}/certificate`;
};

/**
 * Map known certificate asset URLs to same-origin public files.
 *
 * @param {string} url
 * @returns {string}
 */
const rewriteCertAssetUrl = (url) => {
  const raw = String(url || "");
  if (!raw || raw.startsWith("data:")) return raw;
  const base = publicCertBase();
  if (/certificate_template\.png/i.test(raw)) return `${base}/certificate_template.png`;
  if (/certificate_shadow\.png/i.test(raw)) return `${base}/certificate_shadow.png`;
  return raw;
};

/**
 * Repair known broken / alternate S3 paths.
 *
 * @param {string} url
 * @returns {string[]}
 */
const urlCandidates = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return [];
  const clean = raw.split("?")[0];
  const rewritten = rewriteCertAssetUrl(raw);
  const list = [];
  if (rewritten) list.push(rewritten);
  if (clean && clean !== rewritten) list.push(clean);
  if (raw !== clean) list.push(raw);
  // TipTap sometimes drops the /certificate/ folder segment.
  if (/\/v2-assets\/admin\/certificate_(template|shadow)\.png/i.test(clean)) {
    list.push(clean.replace("/v2-assets/admin/", "/v2-assets/admin/certificate/"));
  }
  return [...new Set(list.filter(Boolean))];
};

/**
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Load an image URL into a data URL via fetch (same-origin or CORS).
 *
 * @param {string} url
 * @returns {Promise<string|null>}
 */
const fetchAsDataUrl = async (url) => {
  for (const candidate of urlCandidates(url)) {
    try {
      if (candidate.startsWith("data:")) return candidate;
      const absolute = candidate.startsWith("http") || candidate.startsWith("data:")
        ? candidate
        : new URL(candidate, window.location.href).href;
      const res = await fetch(absolute, {
        mode: "cors",
        credentials: "omit",
        cache: "no-cache",
      });
      if (!res.ok) continue;
      const blob = await res.blob();
      if (!blob || !blob.size) continue;
      return await blobToDataUrl(blob);
    } catch {
      // try next
    }
  }
  return null;
};

/**
 * Fallback: draw via Image + canvas (requires CORS-enabled response).
 *
 * @param {string} url
 * @returns {Promise<string|null>}
 */
const imageElementToDataUrl = (url) =>
  new Promise((resolve) => {
    const candidates = urlCandidates(url);
    let idx = 0;

    const tryNext = () => {
      if (idx >= candidates.length) {
        resolve(null);
        return;
      }
      const candidate = candidates[idx++];
      if (candidate.startsWith("data:")) {
        resolve(candidate);
        return;
      }
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          if (!canvas.width || !canvas.height) {
            tryNext();
            return;
          }
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch {
          tryNext();
        }
      };
      img.onerror = () => tryNext();
      const absolute =
        candidate.startsWith("http") || candidate.startsWith("data:")
          ? candidate
          : new URL(candidate, window.location.href).href;
      img.src = absolute;
    };

    tryNext();
  });

/**
 * Resolve one remote URL to a data URL.
 *
 * @param {string} url
 * @returns {Promise<string|null>}
 */
const toDataUrl = async (url) => {
  const viaFetch = await fetchAsDataUrl(url);
  if (viaFetch) return viaFetch;
  return imageElementToDataUrl(url);
};

/**
 * @param {Element} rootEl
 * @returns {Promise<void>}
 */
export const waitForImages = async (rootEl) => {
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
          // decode() helps when src was just set to a data URL
          if (img.decode) {
            img.decode().then(done).catch(done);
          }
        })
    )
  );
};

/**
 * Replace pending/remote <img> sources with data URLs.
 *
 * @param {Element} rootEl
 * @returns {Promise<void>}
 */
export const inlineImagesForPdf = async (rootEl) => {
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      // Prefer original URL saved before disarm — NOT the placeholder gif on src.
      const pending = img.getAttribute("data-pdf-src");
      const current = img.getAttribute("src") || "";
      const src = pending || current;
      if (!src) return;
      if (!pending && src.startsWith("data:") && src !== PLACEHOLDER_GIF) return;

      img.removeAttribute("crossorigin");
      const dataUrl = await toDataUrl(src);
      if (dataUrl) {
        img.setAttribute("src", dataUrl);
        img.removeAttribute("data-pdf-src");
        return;
      }

      // Soft fallback: point at same-origin cert asset if applicable.
      const local = rewriteCertAssetUrl(src);
      if (local && local !== src && local !== PLACEHOLDER_GIF) {
        img.setAttribute("src", local.startsWith("http") ? local : new URL(local, window.location.href).href);
        img.removeAttribute("data-pdf-src");
      }
    })
  );
  await waitForImages(rootEl);
};

/**
 * Force A4 full-bleed layout on a cloned certificate node.
 *
 * @param {HTMLElement} clone
 */
const forceFullBleedA4 = (clone) => {
  clone.style.cssText = [
    `width:${CERT_A4_WIDTH_PX}px`,
    `height:${CERT_A4_HEIGHT_PX}px`,
    `min-height:${CERT_A4_HEIGHT_PX}px`,
    "max-height:none",
    "margin:0",
    "padding:0",
    "box-sizing:border-box",
    "box-shadow:none",
    "overflow:hidden",
    "background:#ffffff",
    "position:relative",
  ].join(";");

  const frame = clone.querySelector('[data-cert="frame"]') || clone.firstElementChild;
  if (frame) {
    frame.style.cssText = [
      "position:relative",
      `width:${CERT_A4_WIDTH_PX}px`,
      `height:${CERT_A4_HEIGHT_PX}px`,
      `min-height:${CERT_A4_HEIGHT_PX}px`,
      "box-sizing:border-box",
      "margin:0",
      "overflow:hidden",
    ].join(";");
  }

  const bgImg =
    clone.querySelector('[data-cert="bg"]') ||
    Array.from(clone.querySelectorAll("img")).find((img) =>
      /certificate_template/i.test(
        String(img.getAttribute("data-pdf-src") || img.getAttribute("src") || "")
      )
    ) ||
    (frame && frame.querySelector(":scope > img"));
  if (bgImg) {
    bgImg.setAttribute("data-cert", "bg");
    bgImg.style.cssText = [
      "position:absolute",
      "left:0",
      "top:0",
      "width:100%",
      "height:100%",
      "object-fit:cover",
      "object-position:center",
      "z-index:0",
      "display:block",
      "margin:0",
      "padding:0",
      "border:0",
    ].join(";");
  }

  const inner = clone.querySelector('[data-cert="inner"]');
  if (inner) {
    inner.style.position = "relative";
    inner.style.zIndex = "1";
    inner.style.boxSizing = "border-box";
    inner.style.minHeight = `calc(${CERT_A4_HEIGHT_PX}px - 56px)`;
  }
};

/**
 * Stash real src on data-pdf-src and use a tiny placeholder so the browser
 * does not request S3 until we finish inlining.
 *
 * @param {HTMLElement} clone
 */
const disarmRemoteImages = (clone) => {
  Array.from(clone.querySelectorAll("img")).forEach((img) => {
    const src = img.getAttribute("src") || "";
    if (!src || src.startsWith("data:")) return;
    img.setAttribute("data-pdf-src", src);
    img.removeAttribute("crossorigin");
    img.setAttribute("src", PLACEHOLDER_GIF);
  });
};

/**
 * Capture a certificate DOM node to a full-bleed A4 PDF download.
 *
 * @param {HTMLElement} rootEl
 * @param {string} filename
 * @returns {Promise<void>}
 */
export const downloadCertificatePdf = async (rootEl, filename = "certificate.pdf") => {
  if (!rootEl) throw new Error("Certificate root missing");

  const cert = rootEl.querySelector('[data-cert="ampela-root"]') || rootEl;

  const wrap = document.createElement("div");
  wrap.setAttribute("data-cert-pdf-wrap", "1");
  wrap.style.cssText = [
    "position:fixed",
    "left:-12000px",
    "top:0",
    `width:${CERT_A4_WIDTH_PX}px`,
    `height:${CERT_A4_HEIGHT_PX}px`,
    "margin:0",
    "padding:0",
    "box-sizing:border-box",
    "background:#ffffff",
    "overflow:hidden",
    "z-index:-1",
  ].join(";");

  const clone = cert.cloneNode(true);
  forceFullBleedA4(clone);
  disarmRemoteImages(clone);

  // Must read data-pdf-src (not the placeholder gif on src).
  await inlineImagesForPdf(clone);
  forceFullBleedA4(clone);

  wrap.appendChild(clone);
  document.body.appendChild(wrap);

  try {
    await waitForImages(wrap);

    const canvas = await html2canvas(wrap, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      imageTimeout: 20000,
      logging: false,
      width: CERT_A4_WIDTH_PX,
      height: CERT_A4_HEIGHT_PX,
      windowWidth: CERT_A4_WIDTH_PX,
      windowHeight: CERT_A4_HEIGHT_PX,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    pdf.addImage(imgData, "PNG", 0, 0, 210, 297);
    pdf.save(filename);
  } finally {
    wrap.remove();
  }
};
